import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import { ActionIconData, ActionIcons } from "PFComponents/action_icons";
import { Icon } from "PFComponents/icon";
import { LockedIcon } from "PFComponents/text/input_field_set/components/locked_icon";
import { Typography } from "PFComponents/typography";
import { usePreviousValue } from "PFCore/hooks";
import { Option, OptionOriginal } from "PFTypes/options";
import { FocusEvent, KeyboardEvent, RefObject, useEffect, useMemo, useRef, useState } from "react";

import { SelectProps } from "../select.types";
import css from "./value_box.module.scss";
import { ValueContainer } from "./value_container";

const DEFAULT_DISPLAY_VALUES_HEIGHT = 22;
const DISPLAY_VALUES_PADDING_OFFSET = 8;
const DISPLAY_VALUES_BORDER_OFFSET = 2;

type ValueBoxProps<T> = Pick<
  SelectProps<T>,
  | "label"
  | "multiple"
  | "locked"
  | "disabled"
  | "renderDisplayValue"
  | "onChange"
  | "displayValueBelow"
  | "required"
  | "lockedTip"
  | "placeholder"
  | "variant"
  | "tabIndex"
  | "valueBoxWidth"
> & {
  isOpen: boolean;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onClick: VoidFunction;
  onBlur: (event: FocusEvent<HTMLDivElement>) => void;
  actionIcons?: ActionIconData[];
  dropdownId: string;
  highlightedId?: string;
  selectedOptions: Option<T>[];
  isFetching: boolean;
  isError?: boolean;
  ref?: RefObject<HTMLDivElement | null>;
};

export const ValueBox = <T extends OptionOriginal = OptionOriginal>({
  selectedOptions,
  label,
  multiple,
  renderDisplayValue,
  isOpen,
  onKeyDown,
  onClick,
  onChange,
  onBlur,
  actionIcons,
  dropdownId,
  highlightedId,
  locked,
  lockedTip,
  disabled,
  displayValueBelow,
  isFetching,
  isError,
  required,
  placeholder,
  variant,
  tabIndex,
  valueBoxWidth,
  ref
}: ValueBoxProps<T>) => {
  const [style, setStyle] = useState<React.CSSProperties>({});
  const [displayValueRef, setDisplayValueRef] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevSelectedOptionsLength = usePreviousValue(selectedOptions.length);

  const selectedIdsString = selectedOptions.map(({ id }) => id).join(",");

  useEffect(() => {
    if (!displayValueRef) {
      setStyle({});
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      const customHeight = displayValueRef?.getBoundingClientRect().height || 0;

      setStyle({
        height: Math.max(
          customHeight + DISPLAY_VALUES_PADDING_OFFSET + DISPLAY_VALUES_BORDER_OFFSET,
          DEFAULT_DISPLAY_VALUES_HEIGHT + DISPLAY_VALUES_PADDING_OFFSET + DISPLAY_VALUES_BORDER_OFFSET
        )
      });
    });
    resizeObserver.observe(displayValueRef);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!prevSelectedOptionsLength) {
      // do not scroll bottom initially
      return;
    }
    const isValueAdded = prevSelectedOptionsLength < selectedOptions.length;
    if (isValueAdded) {
      containerRef.current?.scrollTo({ top: 100000, behavior: "smooth" });
    }
  }, [selectedOptions.length]);

  const ariaLabel = useMemo(() => {
    if (selectedOptions.length > 0) {
      return `${label}, ${
        multiple ? selectedOptions.map(({ value }) => value).join(", ") : selectedOptions[0].value
      }`;
    } else {
      return label;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiple, label, selectedIdsString]);

  const noOptionsSelected = !isFetching && !selectedOptions.length;

  return (
    <div
      ref={ref}
      role="combobox"
      aria-controls={dropdownId}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-label={ariaLabel}
      aria-required={required}
      aria-invalid={isError ? "true" : undefined}
      aria-activedescendant={highlightedId}
      tabIndex={disabled ? -1 : (tabIndex ?? 0)}
      className={classNames(css.root, {
        [css.disabled]: locked || disabled,
        [css.error]: isError,
        [css.tableVariant]: variant === "table"
      })}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      style={{ ...style, width: valueBoxWidth }}
    >
      {(noOptionsSelected || displayValueBelow) && !!placeholder && (
        <Typography variant="bodyRegular" className={css.placeholder} textOverflow="clip">
          {placeholder}
        </Typography>
      )}
      {!displayValueBelow ? (
        <ValueContainer
          ref={containerRef}
          isFetching={isFetching}
          onChange={onChange}
          renderDisplayValue={renderDisplayValue}
          multiple={multiple}
          setDisplayValueRef={setDisplayValueRef}
          selectedOptions={selectedOptions}
          className={classNames(css.valueContainer, { [css.tableVariant]: variant === "table" })}
          locked={locked}
          disabled={disabled}
        />
      ) : (
        <div className={css.valuePlaceholderElement} />
      )}
      {locked && (
        <LockedIcon
          className={classNames(css.lockedIcon, { [css.tableVariant]: variant === "table" })}
          lockedTip={lockedTip}
        />
      )}
      {!isEmpty(actionIcons) && <ActionIcons actionIcons={actionIcons!} tabIndex={tabIndex} />}
      {!locked && (
        <Icon
          name="chevron-down"
          size="sm"
          className={classNames(css.chevronIcon, { [css.tableVariant]: variant === "table" })}
          color="paletteBlue0"
        />
      )}
    </div>
  );
};
